import { useCallback, useMemo } from 'react';
import { useStores } from 'Hooks/useStore';
import { SystemRoleType } from 'Services/Api/Roles/Types';
import { Status } from 'app-types';

/**
 * Who is allowed to choose the tenant they are reporting on.
 *
 * The sidebar's TenantSelection is the app's existing signal: super admins get
 * the tenant dropdown, while every other user has their single tenant selected
 * for them. Reports follow the same rule — tenant admins pick a tenant, clients
 * are already scoped to one, so their tenant is implied rather than asked for.
 *
 * The selectable tenants are the organizations behind Courses Mapping's
 * "Customers" list, so both screens offer the same set.
 */
export const useTenantScope = () => {
	const { authStore, systemsStore, assignUsersStore } = useStores();

	const canSelectTenant =
		authStore.currentUser?.primarySystemRole?.role ===
		SystemRoleType.SuperAdmin;

	const fetchTenants = useCallback(
		() => assignUsersStore.fetchOrganizations().catch(() => undefined),
		[assignUsersStore]
	);

	const tenantOptions = useMemo(
		() =>
			assignUsersStore.organizations.map((organization) => ({
				value: String(organization.id),
				label: organization.name
			})),
		[assignUsersStore.organizations]
	);

	const isLoadingTenants =
		assignUsersStore.status.fetchOrganizations === Status.Pending;

	/**
	 * The tenant a client is pinned to, taken from the logged in user in the
	 * store context. The sidebar's selection is only a fallback: it resolves
	 * after its tenant list loads, while the current user is already there.
	 */
	const scopedTenantId =
		systemsStore.rootTenant.value;

	const scopedTenantLabel =systemsStore.rootTenant.label;
	const scopedTenant = scopedTenantId ? String(scopedTenantId) : undefined;

	const selectedTenantValue = Number(systemsStore.selectedTenant.value);
	const globalTenantId =
		canSelectTenant && selectedTenantValue > 0
			? selectedTenantValue
			: undefined;

	const groups = authStore.currentUser?.groups?.map((g) => Number(g.id)) ?? [];
	const rootTenantId = Number(systemsStore.rootTenant.value);
	const nonTenantUser =
		!groups.some((groupId) => groupId === rootTenantId);
	return {
		canSelectTenant,
		globalTenantId,
		tenantOptions,
		isLoadingTenants,
		fetchTenants,
		scopedTenant,
		scopedTenantLabel,
		nonTenantUser,
		groups: authStore.currentUser?.groups?.map((g) => g.id),
	};
};

export default useTenantScope;
